import { Variants } from 'framer-motion';

// Core animation variants for gaming feel
export const gameUIVariants: Variants = {
  // Page transitions
  elementEnter: {
    opacity: 0,
    scale: 0.9,
    filter: "blur(10px)",
    transition: { duration: 0.5, ease: "easeOut" }
  },
  elementVisible: {
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 0.5, ease: "easeOut" }
  },
  elementExit: {
    opacity: 0,
    scale: 1.1,
    filter: "blur(10px)",
    transition: { duration: 0.3, ease: "easeIn" }
  },
  
  // Button interactions
  buttonHover: {
    scale: 1.05,
    boxShadow: "0 0 20px rgba(0, 212, 255, 0.8)",
    transition: { duration: 0.2 }
  },
  buttonTap: {
    scale: 0.95,
    transition: { duration: 0.1 }
  },
  buttonGlow: {
    boxShadow: [
      "0 0 5px rgba(0, 212, 255, 0.5)",
      "0 0 20px rgba(0, 212, 255, 0.8)",
      "0 0 5px rgba(0, 212, 255, 0.5)"
    ],
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
  },
  
  // Panel animations
  panelSlide: {
    x: -300,
    opacity: 0,
    transition: { duration: 0.3, ease: "easeInOut" }
  },
  panelVisible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.3, ease: "easeInOut" }
  },
  panelSlideUp: {
    y: 50,
    opacity: 0,
    transition: { duration: 0.4, ease: "easeOut" }
  },
  panelSlideUpVisible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: "easeOut" }
  },
  
  // Card animations
  cardHover: {
    scale: 1.02,
    y: -5,
    boxShadow: "0 10px 30px rgba(0, 212, 255, 0.3)",
    transition: { duration: 0.3, ease: "easeOut" }
  },
  cardTap: {
    scale: 0.98,
    transition: { duration: 0.1 }
  },
  
  // List animations
  listItem: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.3 }
  },
  listItemVisible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3 }
  },
  
  // Modal animations
  modalBackdrop: {
    opacity: 0,
    transition: { duration: 0.3 }
  },
  modalBackdropVisible: {
    opacity: 1,
    transition: { duration: 0.3 }
  },
  modalContent: {
    opacity: 0,
    scale: 0.8,
    y: 50,
    transition: { duration: 0.3, ease: "easeOut" }
  },
  modalContentVisible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" }
  },
  
  // Loading animations
  loadingPulse: {
    scale: [1, 1.1, 1],
    opacity: [0.5, 1, 0.5],
    transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
  },
  loadingRotate: {
    rotate: 360,
    transition: { duration: 2, repeat: Infinity, ease: "linear" }
  },
  
  // Data visualization animations
  chartBar: {
    scaleY: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  },
  chartBarVisible: {
    scaleY: 1,
    transition: { duration: 0.8, ease: "easeOut" }
  },
  
  // Particle animations
  particleFloat: {
    y: [0, -20, 0],
    opacity: [0, 1, 0],
    transition: { duration: 3, repeat: Infinity, ease: "easeInOut" }
  },
  
  // Holographic effects
  hologramFlicker: {
    opacity: [0.3, 0.8, 0.3],
    filter: [
      "hue-rotate(0deg)",
      "hue-rotate(180deg)",
      "hue-rotate(360deg)"
    ],
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" }
  },
  
  // Power-up animations
  powerUp: {
    scale: [0.8, 1.2, 1],
    opacity: [0, 1, 1],
    transition: { duration: 0.5, ease: "easeOut" }
  },
  
  // Scan line effect
  scanLine: {
    x: ["-100%", "100%"],
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
  },
  
  // Glitch effect
  glitch: {
    x: [0, -2, 2, 0],
    y: [0, 1, -1, 0],
    transition: { duration: 0.1, repeat: 3, ease: "easeInOut" }
  },
  
  // Energy flow
  energyFlow: {
    backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
    transition: { duration: 3, repeat: Infinity, ease: "linear" }
  },
  
  // Floating elements
  float: {
    y: [0, -10, 0],
    transition: { duration: 6, repeat: Infinity, ease: "easeInOut" }
  },
  
  // Pulse glow
  pulseGlow: {
    boxShadow: [
      "0 0 5px rgba(0, 212, 255, 0.5)",
      "0 0 20px rgba(0, 212, 255, 0.8)",
      "0 0 5px rgba(0, 212, 255, 0.5)"
    ],
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
  }
};

// Stagger animations for lists
export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

// Page transition variants
export const pageTransition: Variants = {
  initial: {
    opacity: 0,
    x: -20,
    filter: "blur(10px)"
  },
  animate: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  exit: {
    opacity: 0,
    x: 20,
    filter: "blur(10px)",
    transition: {
      duration: 0.3,
      ease: "easeIn"
    }
  }
};

// Hover animations for interactive elements
export const hoverAnimations = {
  button: {
    hover: {
      scale: 1.05,
      boxShadow: "0 0 20px rgba(0, 212, 255, 0.8)",
      transition: { duration: 0.2 }
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 }
    }
  },
  card: {
    hover: {
      scale: 1.02,
      y: -5,
      boxShadow: "0 10px 30px rgba(0, 212, 255, 0.3)",
      transition: { duration: 0.3 }
    }
  },
  link: {
    hover: {
      color: "rgb(0, 212, 255)",
      textShadow: "0 0 10px rgba(0, 212, 255, 0.8)",
      transition: { duration: 0.2 }
    }
  }
};

// Animation presets for different components
export const animationPresets = {
  // Dashboard cards
  dashboardCard: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.4, ease: "easeOut" }
  },
  
  // Navigation items
  navItem: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
    transition: { duration: 0.3, ease: "easeOut" }
  },
  
  // Form inputs
  formInput: {
    focus: {
      boxShadow: "0 0 0 2px var(--stellar-blue)",
      borderColor: "var(--stellar-blue)",
      transition: { duration: 0.2 }
    }
  },
  
  // Loading states
  loading: {
    animate: {
      rotate: 360,
      transition: { duration: 2, repeat: Infinity, ease: "linear" }
    }
  },
  
  // Success/error states
  success: {
    animate: {
      scale: [1, 1.1, 1],
      color: ["var(--success-emerald)", "var(--success-emerald)"],
      transition: { duration: 0.5, ease: "easeOut" }
    }
  },
  
  error: {
    animate: {
      x: [-5, 5, -5, 5, 0],
      color: ["var(--danger-red)", "var(--danger-red)"],
      transition: { duration: 0.3, ease: "easeInOut" }
    }
  }
};

// Utility functions for animations
export const createStaggerAnimation = (delay: number = 0.1) => ({
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: delay,
        delayChildren: delay
      }
    }
  },
  item: {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  }
});

export const createHoverAnimation = (scale: number = 1.05, glow: boolean = true) => ({
  hover: {
    scale,
    ...(glow && { boxShadow: "0 0 20px var(--stellar-blue)" }),
    transition: { duration: 0.2 }
  },
  tap: {
    scale: scale * 0.95,
    transition: { duration: 0.1 }
  }
}); 